import { useEffect, useEffectEvent, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from "react";
import { api } from "./api";
import { Icon, type IconName } from "./icons";
import type { JSONObject } from "./types";

export function Button({ children, wide, tone = "primary", ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { wide?: boolean; tone?: "primary" | "quiet" | "danger" }) {
  return <button className={`button ${tone} ${wide ? "wide" : ""}`} {...props}>{children}</button>;
}

export function Field({ label, hint, ...props }: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string }) {
  return <label className="field"><span>{label}</span><input {...props} />{hint && <small>{hint}</small>}</label>;
}

export function Notice({ children, tone = "info" }: { children: ReactNode; tone?: "info" | "danger" | "success" | "warning" }) {
  return <div className={`notice ${tone}`} role={tone === "danger" ? "alert" : "status"}>{children}</div>;
}

export function Spinner({ small = false }: { small?: boolean }) {
  return <span className={`spinner ${small ? "small" : ""}`} role="status" aria-label="加载中" />;
}

const displayValueLabels: Record<string, string> = {
  active: "启用",
  all: "全部范围",
  live: "实时数据",
  inactive: "未启用",
  disabled: "停用",
  locked: "锁定",
  suspended: "暂停",
  paused: "暂停",
  draft: "草稿",
	created: "待支付",
	pending: "待审核",
  pending_review: "待审核",
  pending_approval: "待审批",
  manual_review: "人工复核",
  applied: "已入账",
  requested: "已申请",
  submitted: "已提交",
  contacting: "联系中",
  issued: "已开具",
  quoted: "已报价",
  accepted: "已接受",
  confirmed: "已确认",
  posted: "已入账",
  investigating: "调查中",
  dismissed: "已驳回",
  adjusted: "已调整",
  amount_mismatch: "金额不一致",
  missing_provider: "缺少供应商用量",
  missing_target: "缺少平台用量",
  purchased: "已购买",
  approved: "已通过",
  rejected: "已拒绝",
  verified: "已验证",
  unverified: "未验证",
  open: "待处理",
  in_progress: "处理中",
  waiting_customer: "待客户",
  resolved: "已解决",
  closed: "已关闭",
  queued: "排队中",
  running: "执行中",
  processing: "处理中",
  succeeded: "已成功",
  failed: "失败",
  sent: "已发送",
  completed: "已完成",
  cancelled: "已取消",
  canceled: "已取消",
  expired: "已过期",
  revoked: "已撤销",
  paid: "已支付",
  refunded: "已退款",
  partially_refunded: "部分退款",
  frozen: "冻结中",
  withdrawable: "可提现",
  reversed: "已冲正",
  offline_receipt: "线下到账",
  payment_repair: "支付补单",
  settlement_difference: "结算差额",
  refund_compensation: "退款补偿",
  promotion: "活动赠送",
  reviewing: "核对中",
  payable: "待结算",
  funds_frozen: "资金已冻结",
  delivering: "交付中",
  delivered: "已交付",
  reserved: "已预留",
  available: "可用",
  allocated: "已分配",
  released: "已释放",
  partially_sold: "部分出售",
  testing: "测试中",
  passed: "已通过",
  invalid: "无效",
  retired: "已退役",
  sold: "已售出",
  current: "当前会话",
  healthy: "健康",
  unhealthy: "异常",
  configured: "已配置",
  unconfigured: "未配置",
  global: "全局范围",
  assigned: "已分配范围",
  organization: "本组织范围",
  admin: "管理员",
  manager: "管理",
  operator: "运营",
  platform: "平台方",
  supplier: "供应商",
  openai: "OpenAI",
  anthropic: "Anthropic Claude",
  openrouter: "OpenRouter",
  aws_bedrock: "AWS Bedrock",
  owner: "负责人",
  platform_owner: "平台负责人",
  platform_finance: "平台财务",
  platform_technical: "平台技术",
  supplier_owner: "供应商负责人",
  supplier_finance: "供应商财务",
  supplier_operator: "供应商运营",
  hosted_newapi: "平台托管 NewAPI",
  external_newapi: "外部 NewAPI",
  newapi: "NewAPI",
  sub2api: "Sub2API",
  lingshu: "百一灵枢",
  tokenrouter: "TokenRouter",
  custom: "定制接口",
  per_usd: "按每美元",
  buyout: "买断",
  exclusive: "独享",
  shared: "共享",
  stocked: "现货",
  quote_only: "仅报价",
  markup: "报价加价",
  brokerage_fee: "中介费",
  fixed_amount: "固定金额",
  percentage_bps: "百分比",
  tiered: "阶梯计价",
  gross_revenue: "平台总收入",
  net_revenue: "平台净收入",
  sales: "销售额",
  api_key: "API Key",
  project_key: "Project Key",
  awsb: "AWSB",
  awsp: "AWSP",
  alipay: "支付宝",
  wechat: "微信",
  usdt: "USDT",
  bank_card: "银行卡",
  payment_issue: "充值问题",
  active_query: "主动查单",
  callback_only: "仅回调",
  original_channel: "原路退款",
  easypay: "易支付协议",
	epusdt_epay: "Epusdt 稳定币收款协议",
  easypay_wxpay: "易支付微信扫码",
  manual_corporate: "对公人工到账",
  alipay_rsa2: "支付宝 RSA2",
  wechatpay_api_v3: "微信支付 API v3",
  signed_usdt: "签名 USDT",
  manual: "人工操作",
  special_vat: "增值税专用发票",
  normal_vat: "增值税普通发票",
  auto_purchase: "自动采购",
  market_purchase: "市场采购",
  delivery_failure: "交付失败",
  credential_unavailable: "KEY 不可用",
  billing_difference: "账单差异",
  refund_dispute: "退款争议",
  withdrawal_issue: "提现问题",
  invoice: "发票",
  account: "账号",
  credit_appeal: "信用申诉",
  credit_profile: "信用档案",
  support_ticket: "服务工单",
  listing: "商品",
  credential: "KEY",
  evidence: "证明材料",
  withdrawal_account: "提现账户",
  balance_adjustment: "余额调整",
  buyer_available: "平台可用余额",
  buyer_frozen: "平台冻结余额",
  refund_payable: "待退余额",
  supplier_pending: "供应商待结算",
  supplier_frozen: "供应商冻结结算",
  supplier_withdrawable: "供应商可提现",
  withdrawal_processing: "提现处理中",
  commission_clawback_receivable: "提成追回应收",
  increase: "增加",
  decrease: "减少",
  payment_credit: "充值入账",
  payment_refund_hold: "充值退款冻结",
  payment_refund_complete: "充值退款完成",
  payment_refund_release: "充值退款释放",
  balance_adjustment_request: "余额调整申请",
  order_funds_frozen: "订单资金冻结",
  buyout_settlement: "买断订单结算",
  settlement_unlock: "结算解锁",
  order_refund: "订单退款",
  commission_accrual: "提成计提",
  commission_reversal: "提成冲正",
  commission_payout: "提成付款",
  withdrawal_reserve: "提现资金冻结",
  withdrawal_paid: "提现打款",
  withdrawal_release: "提现资金释放",
  rpm: "RPM",
  tier: "T 级别",
  quota: "额度",
  billing: "账单",
  other: "其他",
  none: "无",
  order: "订单",
  payment: "支付",
  withdrawal: "提现",
  normal: "普通",
  high: "高",
  urgent: "紧急",
};

export function formatDisplayValue(value: unknown): string {
  const text = String(value ?? "");
  const exact = displayValueLabels[text.toLowerCase()];
  if (exact) return exact;
  const separatorIndex = text.lastIndexOf(" / ");
  if (separatorIndex < 0) return text;
  const suffix = text.slice(separatorIndex + 3);
  const suffixLabel = displayValueLabels[suffix.toLowerCase()];
	return suffixLabel ? `${text.slice(0, separatorIndex)} / ${suffixLabel}` : text;
}

function auditMetadata(row: JSONObject): JSONObject {
	const value = row.metadata;
	if (value && typeof value === "object" && !Array.isArray(value)) return value as JSONObject;
	if (typeof value !== "string" || value.trim() === "") return {};
	try {
		const parsed = JSON.parse(value) as unknown;
		return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed as JSONObject : {};
	} catch {
		return {};
	}
}

export function formatAuditActor(row: JSONObject): string {
	const actor = String(row.actor_user_id ?? "").trim();
	if (actor) return actor;
	const organization = String(row.actor_organization_id ?? "").trim();
	return organization ? `系统任务 · 组织 ${organization.slice(0, 12)}` : "系统任务";
}

export function formatAuditRequest(row: JSONObject): string {
	const requestID = String(row.request_id ?? "").trim();
	if (requestID) return requestID;
	const eventID = String(auditMetadata(row).event_id ?? "").trim();
	return eventID ? `后台任务 · 事件 ${eventID.slice(0, 12)}` : "后台任务";
}

export function formatAuditEvidence(row: JSONObject): string {
	const metadata = auditMetadata(row);
	const parts: string[] = [];
	const version = String(metadata.calculation_version ?? "").trim();
	if (version) parts.push(`计算版本 ${version}`);
	const fromLevel = String(metadata.from_level ?? "").trim();
	const toLevel = String(metadata.to_level ?? "").trim();
	const fromScore = metadata.from_score;
	const toScore = metadata.to_score;
	if (fromLevel || toLevel || fromScore !== undefined || toScore !== undefined) {
		parts.push(`${fromLevel || "-"}/${String(fromScore ?? "-")} -> ${toLevel || "-"}/${String(toScore ?? "-")}`);
	}
	const eventID = String(metadata.event_id ?? "").trim();
	if (eventID) parts.push(`事件 ${eventID.slice(0, 12)}`);
	return parts.join(" · ");
}

function reviewEvidenceRows(details: unknown): Array<[string, string]> {
	if (!details || typeof details !== "object" || Array.isArray(details)) return [];
	const value = details as JSONObject;
	const automated = value.automated_test_result && typeof value.automated_test_result === "object" && !Array.isArray(value.automated_test_result)
		? value.automated_test_result as JSONObject
		: {};
	const rows: Array<[string, string]> = [];
	const add = (label: string, field: unknown, formatter: (item: unknown) => string = formatDisplayValue) => {
		if (field === undefined || field === null || String(field) === "") return;
		rows.push([label, formatter(field)]);
	};
	add("平台", value.provider);
	add("脱密凭据", value.masked_secret, String);
	add("负责人邮箱", value.owner_email, String);
	add("邮箱验证", value.email_verified === true ? "verified" : value.email_verified === false ? "unverified" : undefined);
	add("自动检测", value.automated_test_status);
	add("HTTP 状态", automated.response_code, String);
	add("检测结果", automated.outcome);
	add("错误代码", automated.error_code, String);
	add("模型数量", automated.model_count, String);
	add("检测延时", automated.latency_ms, (item) => `${String(item)} ms`);
	add("调整编号", value.adjustment_number, String);
	add("到账编号", value.arrival_order_number, String);
	if (value.amount !== undefined && value.currency) {
		const micros = Number(value.amount);
		add("业务金额", Number.isFinite(micros) ? `${(micros / 1_000_000).toFixed(6).replace(/\.?0+$/, "")} ${String(value.currency)}` : value.amount, String);
		add("内部微单位", value.amount, String);
	}
	add("申请理由", value.reason, String);
	add("申请人", value.requested_by, String);
	return rows;
}

export function ReviewEvidenceSummary({ details }: { details: unknown }) {
	const rows = reviewEvidenceRows(details);
	if (rows.length === 0) return <span className="muted">没有可展示的结构化证据</span>;
	return <div className="review-evidence-summary">{rows.map(([label, value]) => <span key={label}><small>{label}</small><strong>{value}</strong></span>)}</div>;
}

const orderStatusLabels: Record<string, string> = {
  pending_payment: "待支付",
  funds_frozen: "资金已冻结",
  delivering: "交付中",
  delivered: "已交付",
  active: "使用中",
  completed: "已完成",
  failed: "失败",
  cancelled: "已取消",
  refunding: "退款中",
  refunded: "已退款",
};

export function formatOrderStatus(value: unknown): string {
  const text = String(value ?? "");
  return orderStatusLabels[text.toLowerCase()] ?? formatDisplayValue(text);
}

const listingStatusLabels: Record<string, string> = {
  draft: "草稿",
  pending_review: "待审核",
  active: "已上架",
  paused: "已暂停",
  closed: "已关闭",
};

export function formatListingStatus(value: unknown): string {
  const text = String(value ?? "");
  return listingStatusLabels[text.toLowerCase()] ?? formatDisplayValue(text);
}

export function Badge({ children, tone }: { children: ReactNode; tone?: string }) {
  const value = String(children ?? "").toLowerCase();
  const inferred = tone ?? (/(active|paid|passed|completed|succeeded|sent|resolved|启用|已支付|已通过|已完成|已成功|已发送|已解决|已上架|使用中|可提现)/.test(value) ? "good" : /(failed|rejected|suspended|closed|失败|已拒绝|已关闭)/.test(value) ? "bad" : /(pending|queued|processing|running|review|待|处理中|核对中|交付中|冻结)/.test(value) ? "warn" : "neutral");
  const label = typeof children === "string" ? formatDisplayValue(children) : children;
  return <span className={`badge ${inferred}`}>{label}</span>;
}

type SurfaceMode = "overview" | "list" | "report" | "workspace" | "settings" | "detail" | "create" | "edit" | "action";

const surfaceModeLabel: Record<SurfaceMode, string> = {
  overview: "概览",
  list: "列表",
  report: "数据分析",
  workspace: "工作台",
  settings: "设置",
  detail: "详情",
  create: "新建",
  edit: "编辑",
  action: "业务处理",
};

const pageSectionLabel: Record<string, string> = {
  "LIVE BOARD": "工作台",
  "ANONYMOUS MARKET": "采购管理",
  "ORDER LEDGER": "订单管理",
  "ROUTING FABRIC": "交付与数据",
  AUTOBUY: "采购管理",
  "MONEY LEDGER": "资金管理",
  "SERVICE DESK": "组织管理",
  "TRUST CENTER": "组织管理",
  "SUPPLY DESK": "商品管理",
  "SUPPLIER API": "接口与数据",
  GOVERNANCE: "客户管理",
  TAXONOMY: "商品治理",
  "OPERATIONS REPORT": "数据中心",
  "USAGE TELEMETRY": "数据中心",
  "SYSTEM ADMINISTRATION": "平台治理",
  "TRUST SCORE": "组织管理",
};

function sectionFromIndex(index: string) {
  const source = index.split("/").at(-1)?.trim() ?? index;
  return pageSectionLabel[source] ?? source;
}

function modeFromTitle(title: string): SurfaceMode {
  if (/(新建|新增|创建|添加|邀请|录入|导入|发布)/.test(title)) return "create";
  if (/(编辑|修改|调整|设置|配置|分配|变更)/.test(title)) return "edit";
  if (/(详情|明细|记录|库存|账单核对)/.test(title)) return "detail";
  return "action";
}

export function PageHeader({ index, title, description, actions, mode = "list", section, viewHint }: {
  index: string;
  title: string;
  description?: string;
  actions?: ReactNode;
  mode?: SurfaceMode;
  section?: string;
  viewHint?: string;
}) {
  const defaultViewHint = mode === "list"
    ? "按条件筛选并管理记录"
    : mode === "report" ? "指标、趋势与明细" : "";
  return <header className="page-header" data-surface-mode={mode}>
    <div>
      <div className="page-context"><strong>{surfaceModeLabel[mode]}</strong><span>{section ?? sectionFromIndex(index)}</span></div>
      <div className="page-title-row"><h1>{title}</h1>{(viewHint ?? defaultViewHint) && <span className="page-view-hint">{viewHint ?? defaultViewHint}</span>}</div>
      {description && <p>{description}</p>}
    </div>
    {actions && <div className="page-actions">{actions}</div>}
  </header>;
}

export function MetricCard({ label, value, detail, accent = "lime", icon }: { label: string; value: ReactNode; detail?: string; accent?: "lime" | "orange" | "blue" | "graphite"; icon?: IconName }) {
  const fallbackIcon: Record<string, IconName> = { lime: "activity", orange: "inventory", blue: "reports", graphite: "security" };
  return <article className={`metric-card ${accent}`}><header><span className="metric-icon"><Icon name={icon ?? fallbackIcon[accent] ?? "activity"} /></span><span>{label}</span></header><strong>{value}</strong>{detail && <small>{detail}</small>}</article>;
}

export function Empty({ title = "暂无数据", detail }: { title?: string; detail?: string }) {
  return <div className="empty"><span>∅</span><h3>{title}</h3>{detail && <p>{detail}</p>}</div>;
}

export function Modal({ title, onClose, children, mode }: { title: string; onClose: () => void; children: ReactNode; mode?: SurfaceMode }) {
  useEffect(() => {
    const close = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onClose]);
  const resolvedMode = mode ?? modeFromTitle(title);
  return <div className="modal-layer" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
    <section className="modal" role="dialog" aria-modal="true" aria-label={title} data-surface-mode={resolvedMode}>
      <header><div className="surface-heading"><strong>{surfaceModeLabel[resolvedMode]}</strong><h2>{title}</h2></div><button type="button" aria-label="关闭" onClick={onClose}><Icon name="close" /></button></header>
      {children}
    </section>
  </div>;
}

export function Drawer({ title, subtitle, onClose, children, mode = "detail" }: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  mode?: SurfaceMode;
}) {
  useEffect(() => {
    const close = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onClose]);
  return <div className="entity-drawer-layer" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
    <aside className="entity-drawer" role="dialog" aria-modal="true" aria-label={title} data-surface-mode={mode}>
      <header>
        <div className="surface-heading"><strong>{surfaceModeLabel[mode]}</strong><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>
        <button type="button" aria-label="关闭" onClick={onClose}><Icon name="close" /></button>
      </header>
      <div className="entity-drawer-body">{children}</div>
    </aside>
  </div>;
}

export function DataTable({ rows, columns, keyField = "id", emptyDetail, pageSize = 20, pagination }: {
  rows: JSONObject[];
  columns: Array<{ key: string; label: string; render?: (row: JSONObject) => ReactNode }>;
  keyField?: string;
  emptyDetail?: string;
  pageSize?: number;
  pagination?: { page: number; pageSize: number; total: number; onPageChange: (page: number) => void; loading?: boolean };
}) {
  const effectivePageSize = pagination?.pageSize ?? (pageSize && pageSize > 0 ? pageSize : Math.max(rows.length, 1));
  const totalRows = pagination?.total ?? rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / effectivePageSize));
  const [localPage, setLocalPage] = useState(1);
  const page = pagination?.page ?? localPage;
  useEffect(() => setLocalPage((current) => Math.min(current, totalPages)), [totalPages]);
  const clampServerPage = useEffectEvent((nextPage: number) => pagination?.onPageChange(nextPage));
  useEffect(() => {
    if (pagination && pagination.page > totalPages) clampServerPage(totalPages);
  }, [pagination?.page, totalPages]);
  if (!rows.length) return <Empty detail={emptyDetail} />;
  const visibleRows = pagination ? rows : pageSize ? rows.slice((page - 1) * effectivePageSize, page * effectivePageSize) : rows;
  const changePage = (nextPage: number) => {
    if (pagination) pagination.onPageChange(nextPage);
    else setLocalPage(nextPage);
  };
  return <div className="table-stack">
    <div className="table-meta"><span>数据列表</span><strong>共 {totalRows} 条记录</strong></div>
    <div className="table-wrap"><table><thead><tr>{columns.map((column) => <th key={column.key} data-column={column.key}>{column.label}</th>)}</tr></thead><tbody>{visibleRows.map((row, index) => <tr key={String(row[keyField] ?? ((page - 1) * effectivePageSize + index))}>{columns.map((column) => <td key={column.key} data-column={column.key} data-label={column.label}>{column.render ? column.render(row) : formatCell(row[column.key])}</td>)}</tr>)}</tbody></table></div>
    {(pagination || pageSize) && totalPages > 1 && <nav className="table-pagination" aria-label="表格分页">
      <span aria-live="polite">第 {page} / {totalPages} 页，共 {totalRows} 条</span>
      <div>
        <button type="button" onClick={() => changePage(Math.max(1, page - 1))} disabled={page === 1 || pagination?.loading}>上一页</button>
        <button type="button" onClick={() => changePage(Math.min(totalPages, page + 1))} disabled={page === totalPages || pagination?.loading}>下一页</button>
      </div>
    </nav>}
  </div>;
}

export function formatCell(value: unknown): ReactNode {
  if (value === null || value === undefined || value === "") return <span className="muted">—</span>;
  if (typeof value === "boolean") return value ? "是" : "否";
  if (Array.isArray(value)) return value.length ? value.map(formatDisplayValue).join(" / ") : <span className="muted">—</span>;
  if (typeof value === "object") return <code className="json-cell">{JSON.stringify(value)}</code>;
  const text = String(value);
  if (/^\d{4}-\d\d-\d\dT/.test(text)) return new Intl.DateTimeFormat("zh-CN", { dateStyle: "medium", timeStyle: "short" }).format(new Date(text));
  return formatDisplayValue(text);
}

export function useResource<T>(path: string | null, initial: T) {
  const [data, setData] = useState<T>(initial);
  const [loading, setLoading] = useState(Boolean(path));
  const [error, setError] = useState("");
  const load = async (signal?: AbortSignal) => {
    if (!path) {
      setLoading(false);
      setError("");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const result = await api<T>(path, { signal });
      if (!signal?.aborted) setData(result);
    } catch (caught) {
      if (!signal?.aborted) setError(caught instanceof Error ? caught.message : "读取失败");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };
  const loadEvent = useEffectEvent(load);
  useEffect(() => {
    const controller = new AbortController();
    void loadEvent(controller.signal);
    return () => controller.abort();
  }, [path]);
  return { data, loading, error, refresh: () => load(), setData };
}

export function ResourceState({ loading, error, children }: { loading: boolean; error: string; children: ReactNode }) {
  if (loading) return <div className="page-loading"><Spinner /><span>正在读取实时数据</span></div>;
  if (error) return <Notice tone="danger">{error}</Notice>;
  return <>{children}</>;
}
